import { parseChord } from './chord'
import { formatKey, inferKey, parseKey, spellPitchClass, transposeKey, type Key } from './key'
import { formatNote, pitchClass, type Notation } from './notes'
import type { Provenance, RevisionContent } from '../schema/v1'

/**
 * Transposición y cejilla (§3.3). Son dos operaciones distintas:
 *
 * - **Transponer** cambia el sonido: las formas de los dedos y el tono que suena suben o bajan lo
 *   mismo, y la cejilla no se toca.
 * - **Cejilla** cambia la forma de los dedos sin cambiar el sonido: al subir un traste, todas las
 *   formas bajan un semitono.
 *
 * Los acordes de una revisión son las formas que tocas. Lo que suena es la forma más la cejilla.
 * Ninguna operación toca los IDs, el texto ni los anclajes: solo reescribe símbolos de acordes.
 */
const mod = (value: number, modulus: number) => ((value % modulus) + modulus) % modulus

/** Mueve un acorde, escrito en la notación en que venía, y lo deletrea según el tono de destino. */
export function transposeSymbol(symbol: string, semitones: number, key: Key | null): string {
  if (mod(semitones, 12) === 0) return symbol
  const parsed = parseChord(symbol)
  if (!parsed || parsed.kind !== 'chord') return symbol
  const notation: Notation = parsed.notation
  const root = spellPitchClass(pitchClass(parsed.root) + semitones, key)
  let result = formatNote(root, notation) + parsed.suffix
  if (parsed.bass) {
    result += '/' + formatNote(spellPitchClass(pitchClass(parsed.bass) + semitones, key), notation)
  }
  return result
}

function allChordSymbols(content: RevisionContent): string[] {
  return content.sections.flatMap((section) =>
    section.lines.flatMap((line) => line.chords.map((anchor) => anchor.chord)),
  )
}

/** Tono de las formas: el indicado en la ficha o, si falta, una estimación por los acordes. */
export function shapeKeyOf(content: RevisionContent): Key | null {
  return (
    (content.shapeKey ? parseKey(content.shapeKey) : null) ?? inferKey(allChordSymbols(content))
  )
}

/** Tono que suena: el de las formas subido tantos trastes como indique la cejilla. */
export function soundingKeyOf(content: RevisionContent): Key | null {
  const shape = shapeKeyOf(content)
  return shape ? transposeKey(shape, content.capo) : null
}

/** ¿El tono de la ficha lo escribió alguien o lo estimó el programa? */
export function isKeyEstimated(content: RevisionContent): boolean {
  return !content.shapeKey || parseKey(content.shapeKey) === null
}

function mapChords(
  content: RevisionContent,
  convert: (symbol: string) => string,
): RevisionContent['sections'] {
  return content.sections.map((section) => ({
    ...section,
    lines: section.lines.map((line) => ({
      ...line,
      chords: line.chords.map((anchor) => ({ ...anchor, chord: convert(anchor.chord) })),
    })),
  }))
}

/** Notación en la que se escribe el tono: la del tono de la ficha o, si falta, la de los acordes. */
function keyNotation(content: RevisionContent): Notation {
  const parsed = content.shapeKey ? parseChord(content.shapeKey) : null
  return parsed?.kind === 'chord' ? parsed.notation : content.notation
}

function withKeys(
  content: RevisionContent,
  sections: RevisionContent['sections'],
  capo: number,
  shape: Key | null,
  method: string,
): RevisionContent {
  const estimated = isKeyEstimated(content)
  const notation = keyNotation(content)
  const provenance: Record<string, Provenance> = { ...content.provenance }
  const next: RevisionContent = { ...content, sections, capo, provenance }
  if (shape) {
    next.shapeKey = formatKey(shape, notation)
    // Un tono que se estimó sigue siendo una estimación aunque se haya movido.
    provenance.shapeKey = estimated
      ? { kind: 'rule', method: `infer-key+${method}`, needsReview: true }
      : { kind: 'user', method }
    if (capo > 0 || content.soundingKey) {
      next.soundingKey = formatKey(transposeKey(shape, capo), notation)
    }
  }
  return next
}

/**
 * Transpone la ficha: formas y tono que suena se mueven los mismos semitonos; la cejilla se
 * queda como está. Para cambiar solo el sonido sin cambiar las formas, sube la cejilla a la vez
 * (`transposeContent` y después `setCapo` con el traste nuevo).
 */
export function transposeContent(content: RevisionContent, semitones: number): RevisionContent {
  if (!Number.isInteger(semitones)) throw new RangeError('Los semitonos tienen que ser enteros')
  if (mod(semitones, 12) === 0) return content
  const shape = shapeKeyOf(content)
  const target = shape ? transposeKey(shape, semitones) : null
  const sections = mapChords(content, (symbol) => transposeSymbol(symbol, semitones, target))
  return withKeys(content, sections, content.capo, target, `transpose:${semitones}`)
}

/**
 * Pone la cejilla en otro traste sin cambiar el sonido: las formas se mueven al revés que la
 * cejilla. Con la cejilla en el 3, un La menor que suena como Do menor se toca con la forma Am.
 */
export function setCapo(content: RevisionContent, capo: number): RevisionContent {
  if (!Number.isInteger(capo) || capo < 0 || capo > 12) {
    throw new RangeError('La cejilla va del traste 0 al 12')
  }
  if (capo === content.capo) return content
  const delta = capo - content.capo
  const shape = shapeKeyOf(content)
  const target = shape ? transposeKey(shape, -delta) : null
  const sections = mapChords(content, (symbol) => transposeSymbol(symbol, -delta, target))
  return withKeys(content, sections, capo, target, `capo:${capo}`)
}
